import { useId, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { type Align, toMarkdownTable } from "../lib/mdtable";

const MAX_ROWS = 20;
const MAX_COLS = 10;

const DEFAULT_GRID = [
  ["Name", "Role", "Team"],
  ["Ada", "Engineer", "Core"],
  ["Grace", "Admiral", "Navy"],
];

const ALIGN_OPTIONS: Align[] = ["left", "center", "right", "none"];

const selectClass =
  "h-9 w-full rounded-md border border-border bg-card px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function MarkdownTableTool() {
  const uid = useId();
  const rowIdRef = useRef(0);
  const colIdRef = useRef(0);
  const [grid, setGrid] = useState<string[][]>(DEFAULT_GRID);
  const [rowIds, setRowIds] = useState<string[]>(() =>
    DEFAULT_GRID.map(() => `r${rowIdRef.current++}`),
  );
  const [colIds, setColIds] = useState<string[]>(() =>
    DEFAULT_GRID[0].map(() => `c${colIdRef.current++}`),
  );
  const [aligns, setAligns] = useState<Align[]>(() =>
    DEFAULT_GRID[0].map(() => "left"),
  );
  const [copied, setCopied] = useState(false);

  const cols = colIds.length;
  const output = useMemo(
    () => toMarkdownTable(grid, { align: aligns, header: true }),
    [grid, aligns],
  );

  const setCell = (r: number, c: number, value: string) => {
    setGrid((prev) =>
      prev.map((row, ri) =>
        ri === r ? row.map((cell, ci) => (ci === c ? value : cell)) : row,
      ),
    );
  };

  const addRow = () => {
    if (rowIds.length >= MAX_ROWS) return;
    setGrid((prev) => [...prev, new Array(cols).fill("")]);
    setRowIds((prev) => [...prev, `r${rowIdRef.current++}`]);
  };
  const removeRow = () => {
    if (rowIds.length <= 1) return;
    setGrid((prev) => prev.slice(0, -1));
    setRowIds((prev) => prev.slice(0, -1));
  };
  const addCol = () => {
    if (cols >= MAX_COLS) return;
    setGrid((prev) => prev.map((row) => [...row, ""]));
    setColIds((prev) => [...prev, `c${colIdRef.current++}`]);
    setAligns((prev) => [...prev, "left"]);
  };
  const removeCol = () => {
    if (cols <= 1) return;
    setGrid((prev) => prev.map((row) => row.slice(0, -1)));
    setColIds((prev) => prev.slice(0, -1));
    setAligns((prev) => prev.slice(0, -1));
  };

  const setAlign = (c: number, value: Align) => {
    setAligns((prev) => prev.map((a, ci) => (ci === c ? value : a)));
  };

  const copy = () => {
    if (typeof navigator === "undefined") return;
    navigator.clipboard?.writeText(output).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-1">
            <thead>
              <tr>
                {colIds.map((cid, c) => (
                  <th key={cid} className="min-w-[8rem]">
                    <select
                      aria-label={`Column ${c + 1} alignment`}
                      className={selectClass}
                      value={aligns[c]}
                      onChange={(e) => setAlign(c, e.target.value as Align)}
                    >
                      {ALIGN_OPTIONS.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowIds.map((rid, r) => (
                <tr key={rid}>
                  {colIds.map((cid, c) => (
                    <td key={`${rid}-${cid}`}>
                      <Input
                        aria-label={`Row ${r + 1} column ${c + 1}`}
                        value={grid[r]?.[c] ?? ""}
                        onChange={(e) => setCell(r, c, e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                        className="text-sm"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={addRow} className={btnClass}>
            {m.mt_add_row()}
          </button>
          <button type="button" onClick={removeRow} className={btnClass}>
            {m.mt_remove_row()}
          </button>
          <button type="button" onClick={addCol} className={btnClass}>
            {m.mt_add_col()}
          </button>
          <button type="button" onClick={removeCol} className={btnClass}>
            {m.mt_remove_col()}
          </button>
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor={`${uid}-out`}>{m.mt_output_label()}</Label>
          <button type="button" onClick={copy} className={btnClass}>
            {copied ? m.mt_copied() : m.mt_copy()}
          </button>
        </div>
        <textarea
          id={`${uid}-out`}
          data-testid="md-output"
          readOnly
          value={output}
          className="min-h-[180px] w-full rounded-md border border-border bg-card px-3 py-2 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
    </div>
  );
}

const btnClass =
  "rounded-md border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground";
